import React, { useMemo, useState, useEffect } from 'react'
import { useNextCalendarApp, ScheduleXCalendar } from '@schedule-x/react'
import {
  createViewDay,
  createViewMonthAgenda,
  createViewMonthGrid,
  createViewWeek,
} from '@schedule-x/calendar'
import { createEventsServicePlugin } from '@schedule-x/events-service'
import { createCalendarControlsPlugin } from '@schedule-x/calendar-controls'
import { createDragAndDropPlugin } from '@schedule-x/drag-and-drop'
import 'temporal-polyfill/global'
import '@schedule-x/theme-default/dist/index.css'
import type { Task } from '../../hooks/useTasks'

interface CalendarChartProps {
  tasks: Task[]
  criticalPath?: number[]
  height?: number
}

export const CalendarChart: React.FC<CalendarChartProps> = ({
  tasks,
  criticalPath = [],
  height = 400
}) => {
  const [activeDate, setActiveDate] = useState<Date | null>(null)

  const eventsService = useState(() => createEventsServicePlugin())[0]
  const calendarControls = useState(() => createCalendarControlsPlugin())[0]

  const calendarApp = useNextCalendarApp({
    views: [createViewDay(), createViewWeek(), createViewMonthGrid(), createViewMonthAgenda()],
    defaultView: 'month-grid',
    locale: 'en-US',
    firstDayOfWeek: 1,
    timezone: 'UTC',
    dayBoundaries: {
      start: '06:00',
      end: '22:00'
    },
    calendars: {
      work: {
        colorName: 'work',
        lightColors: {
          main: '#3B82F6',
          container: '#DBEAFE',
          onContainer: '#1E40AF'
        },
        darkColors: {
          main: '#60A5FA',
          onContainer: '#EFF6FF',
          container: '#2563EB'
        }
      },
      critical: {
        colorName: 'critical',
        lightColors: {
          main: '#EF4444',
          container: '#FEE2E2',
          onContainer: '#991B1B'
        },
        darkColors: {
          main: '#F87171',
          onContainer: '#FEE2E2',
          container: '#DC2626'
        }
      }
    },
    events: [],
    plugins: [
      eventsService,
      calendarControls,
      createDragAndDropPlugin()
    ],
    callbacks: {
      onClickDate(date) {
        setActiveDate(new Date(date.toString()))
      },
      onRangeUpdate(range) {
        console.log('Range updated:', range.start.toString(), range.end.toString())
      }
    }
  })

  useEffect(() => {
    if (tasks && tasks.length > 0) {
      const events = tasks.map(task => {
        try {
          const startDate = new Date(task.startDate)
          const endDate = new Date(task.endDate)
          
          // Validasi tanggal
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            console.warn(`Invalid date for task ${task.id}: ${task.taskName}`)
            return null
          }
          
          return {
            id: task.id.toString(),
            title: task.taskName,
            start: Temporal.PlainDate.from(startDate.toISOString().split('T')[0]),
            end: Temporal.PlainDate.from(endDate.toISOString().split('T')[0]),
            calendarId: criticalPath.includes(task.id) ? 'critical' : 'work',
            location: task.status,
            description: `Duration: ${task.duration} days`
          }
        } catch (error) {
          console.error(`Error processing task ${task.id}:`, error)
          return null
        }
      }).filter(Boolean) as any[]
      
      eventsService.set(events)
    }
  }, [tasks, criticalPath, eventsService])

  if (tasks.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded border-2 border-dashed border-gray-300">
        <p className="text-gray-500">No tasks available. Generate tasks from BoQ first.</p>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="calendar-container" style={{ height: `${height}px` }}>
        <ScheduleXCalendar calendarApp={calendarApp} />
      </div>
      
      <div className="mt-4 p-4 bg-blue-50 rounded border border-blue-100">
        <h3 className="font-semibold text-blue-900 mb-2">Calendar Information</h3>
        <p className="text-sm text-blue-800">
          <span className="font-medium">Total Tasks:</span> {tasks.length} tasks displayed
          <br />
          <span className="font-medium">Critical Path Tasks:</span> {tasks.filter(task => criticalPath.includes(task.id)).length}
        </p>
      </div>

      {activeDate && (
        <div className="mt-4 p-4 bg-gray-50 rounded border">
          <h3 className="font-semibold text-gray-900 mb-2">
            Tasks on {activeDate.toLocaleDateString()}
          </h3>
          <ul className="text-sm text-gray-700 space-y-1">
            {tasks
              .filter(task => 
                new Date(task.startDate).toISOString().split('T')[0] === activeDate.toISOString().split('T')[0]
              )
              .map((task) => (
                <li
                  key={task.id}
                  className={`p-2 rounded ${criticalPath.includes(task.id) ? 'bg-red-50 border-l-4 border-red-500' : 'bg-white border-l-4 border-blue-500'}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{task.taskName}</p>
                      <p className="text-xs text-gray-500 capitalize">{task.status}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${task.status === 'completed' ? 'bg-green-100 text-green-800' : task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : task.status === 'delayed' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                      {task.duration} days
                    </span>
                  </div>
                </li>
              ))}
          </ul>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-600 space-y-1">
        <p><span className="inline-block w-3 h-3 bg-red-500 mr-2"></span>Critical Day: Contains tasks on the critical path</p>
        <p><span className="inline-block w-3 h-3 bg-blue-500 mr-2"></span>Task Day: Contains regular tasks</p>
      </div>
    </div>
  )
}